import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { awsConfig } from './aws-config';

const poolData = {
  UserPoolId: awsConfig.userPoolId,
  ClientId: awsConfig.userPoolWebClientId
};

const userPool = new CognitoUserPool(poolData);

// Helper do wywołań auth proxy
async function authProxyRequest(endpoint, body) {
  const response = await fetch(`${awsConfig.authProxyUrl}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(body)
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Request failed');
    error.statusCode = response.status;
    throw error;
  }

  return data;
}

export const checkEmail = async (email) => {
  return authProxyRequest('/auth/check-email', { email });
};

export const signIn = async ({ email, password, redirectToClient }) => {
  const data = await authProxyRequest('/auth/login', { email, password });

  // Handle NEW_PASSWORD_REQUIRED challenge
  if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
    const changePasswordUrl = new URL(window.location.origin + '/change-password');
    changePasswordUrl.searchParams.set('session', data.session);
    changePasswordUrl.searchParams.set('email', email);
    if (redirectToClient) {
      changePasswordUrl.searchParams.set('redirectToClient', redirectToClient);
    }
    window.location.href = changePasswordUrl.toString();
    return;
  }

  // Redirect with temp auth code (same flow as OAuth)
  if (redirectToClient && data.code) {
    const callbackUrl = new URL(window.location.origin + '/callback');
    callbackUrl.searchParams.set('code', data.code);
    callbackUrl.searchParams.set('state', JSON.stringify({ redirectToClient }));

    window.location.href = callbackUrl.toString();
  }

  return data;
};

export const signOut = () => {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
};

export const signInWithGoogle = (redirectToClient = null) => {
  let cognitoHostedUiUrl = `https://${awsConfig.oauth.domain}/oauth2/authorize?client_id=${awsConfig.userPoolWebClientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(awsConfig.oauth.redirectSignIn)}&identity_provider=Google&prompt=select_account`;

  if (redirectToClient) {
    const state = encodeURIComponent(JSON.stringify({ redirectToClient }));
    cognitoHostedUiUrl += `&state=${state}`;
  }

  window.location.href = cognitoHostedUiUrl;
};

export const signInWithFacebook = (redirectToClient = null) => {
  const base = `https://${awsConfig.oauth.domain}/oauth2/authorize`;
  const params = new URLSearchParams({
    client_id: awsConfig.userPoolWebClientId,
    response_type: 'code',
    scope: 'email openid profile',
    redirect_uri: awsConfig.oauth.redirectSignIn,
    identity_provider: 'Facebook'
  });

  if (redirectToClient) {
    params.set('state', JSON.stringify({ redirectToClient }));
  }

  window.location.href = `${base}?${params.toString()}`;
};

export const respondToNewPasswordRequired = async ({ email, newPassword, session, redirectToClient }) => {
  const data = await authProxyRequest('/auth/respond-challenge', {
    email,
    newPassword,
    session
  });

  if (redirectToClient && data.code) {
    const callbackUrl = new URL(window.location.origin + '/callback');
    callbackUrl.searchParams.set('code', data.code);
    callbackUrl.searchParams.set('state', JSON.stringify({ redirectToClient }));

    window.location.href = callbackUrl.toString();
  }

  return data;
};

export const forgotPassword = async (email, redirectToClient = null) => {
  return authProxyRequest('/auth/forgot-password', { email });
};

export const confirmPassword = async (email, code, newPassword, redirectToClient = null) => {
  return authProxyRequest('/auth/confirm-password', {
    email,
    code,
    newPassword
  });
};

export const handleOAuthCallback = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');

  if (!code) {
    throw new Error('No authorization code found');
  }

  let redirectToClient = null;
  if (state) {
    try {
      const stateData = JSON.parse(decodeURIComponent(state));
      redirectToClient = stateData.redirectToClient;
    } catch (error) {
      console.warn('Failed to parse state parameter:', error);
    }
  }

  // Exchange code via auth proxy (clientSecret stays on backend)
  const tokens = await authProxyRequest('/auth/oauth/token', {
    code,
    redirectUri: awsConfig.oauth.redirectSignIn
  });

  if (tokens.accessToken) {
    localStorage.setItem('access_token', tokens.accessToken);
    localStorage.setItem('id_token', tokens.idToken);
    if (tokens.refreshToken) {
      localStorage.setItem('refresh_token', tokens.refreshToken);
    }
    return { tokens, redirectToClient };
  } else {
    throw new Error('No access token received');
  }
};
